import React from "react";
import ReactDOM from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import {
  Users, UserPlus, Search, EyeOff, Link2, Network,
  Mail, Phone, Building2, Crown, AlertTriangle, Copy, UserCog, Briefcase,
  Upload, Archive, Download, ChevronDown, FileSpreadsheet,
  Monitor, ListFilter, ShieldCheck, Pencil, Shield, Check,
  LayoutGrid, LayoutList, X,
} from "lucide-react";
import { useDashTheme } from "../hooks/useDashTheme";
import { useTeamData } from "../hooks/useTeamData";
import { PageShell } from "../components/PageShell";
import { Card, Button, Badge, Avatar } from "../components/primitives";
import { DataTable, EmptyState, Pagination } from "../components/DataTable";
import { ToolSelect } from "../components/ToolSelect";
import { Drawer } from "../components/Drawer";
import { Modal } from "../components/forms";
import { isCrmEnabledForOrg } from "../../../modules/crm/utils/orgGating";
import { useSlotGate } from "../components/TeamSlotsPayment";
import { BulkImportDrawer } from "../components/BulkImportDrawer";
import { MemberFormDrawer } from "./team/MemberFormDrawer";
import { MemberPermissionsPanel } from "./team/MemberPermissionsPanel";
import { teamColumns } from "./team/teamColumns";
import { TeamPulse } from "./team/TeamPulse";
import { OrgLevelsPanel, ReportingLinesPanel } from "./team/TeamStructurePanels";
import { MemberCardGrid } from "./team/MemberCards";
import {
  compositionBreakdown, coverageMetrics, attentionCounts, attentionLabel,
  orgLevels, managerSpans, isOrphan,
} from "./team/teamInsights";
import {
  displayMemberEmail, resolveReportingManagers, formatManagerName, memberName,
  roleLabel, normalizeReportingManagerIds, MEMBER_STATUSES, AGENT_FILTERS,
} from "./team/teamHelpers";
import { buildMemberExportRows, exportMembersCsv, exportMembersXlsx, getMembersReportingTo } from "../utils/teamMemberExport";
import { DASHBOARD_BASE } from "../../../config/featureFlags";

const VIEW_KEY = "tg.team.view";

const blankUser = {
  firstName: "", lastName: "", email: "", stubEmail: "", role: "org_user", phone: "",
  department: "", position: "", reportingManagers: [], billingEnabled: true,
  password: "", stealth_mode: false, invitationMethod: "manual",
  permissions: { addProject: false, addTeamMember: false, viewReports: true, viewActivity: true, editProfile: true, crmAccess: false },
};

function ExportDropdown({ t, open, anchorRef, menuRef, onCsv, onXlsx }) {
  const [pos, setPos] = React.useState({ top: 0, left: 0, width: 196 });

  React.useLayoutEffect(() => {
    if (!open || !anchorRef.current) return undefined;
    const update = () => {
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 6, left: rect.right - 196, width: 196 });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, anchorRef]);

  if (!open) return null;

  const itemStyle = (withBorderTop = false) => ({
    width: "100%",
    padding: "10px 14px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    border: "none",
    borderTop: withBorderTop ? `1px solid ${t.c.border}` : "none",
    background: "transparent",
    color: t.c.text,
    fontSize: t.font.size.sm,
    fontFamily: t.font.family,
    cursor: "pointer",
    textAlign: "left",
  });

  return ReactDOM.createPortal(
    <div
      ref={menuRef}
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        width: pos.width,
        zIndex: 10050,
        background: t.c.surface,
        border: `1px solid ${t.c.border}`,
        borderRadius: t.r.md,
        boxShadow: t.shadow.lg,
        overflow: "hidden",
      }}
    >
      <button type="button" onClick={onCsv} style={itemStyle()}>
        <Download size={14} /> CSV
      </button>
      <button type="button" onClick={onXlsx} style={itemStyle(true)}>
        <FileSpreadsheet size={14} /> Excel (.xlsx)
      </button>
    </div>,
    document.body,
  );
}

// Map API validation / hierarchy errors onto form fields for inline display.
function mapBackendFieldErrors(err) {
  const data = err?.response?.data;
  const messages = [];
  if (Array.isArray(data?.error?.details)) messages.push(...data.error.details);
  else if (Array.isArray(data?.errors)) messages.push(...data.errors);
  else if (data?.error?.message) messages.push(data.error.message);
  else if (data?.message) messages.push(data.message);

  const errs = {};
  for (const msg of messages) {
    const s = String(msg || "").toLowerCase();
    if (s.includes("stub email")) errs.stubEmail = msg;
    else if (s.includes("reporting manager")) errs.reportingManagers = msg;
    else if (s.includes("first name")) errs.firstName = msg;
    else if (s.includes("last name")) errs.lastName = msg;
    else if (s.includes("email")) errs.email = msg;
    else if (s.includes("password")) errs.password = msg;
    else if (s.includes("position")) errs.position = msg;
  }
  return errs;
}

export default function TeamV2() {
  const t = useDashTheme();
  const d = useTeamData();
  const navigate = useNavigate();
  const slotGate = useSlotGate();

  // Members and Org structure are separate sidebar destinations, so the route
  // decides which view this screen renders instead of an in-page tab.
  const structureView = useLocation().pathname.includes("/org-structure");
  const [searchInput, setSearchInput] = React.useState("");
  React.useEffect(() => { const id = setTimeout(() => d.setSearch(searchInput), 350); return () => clearTimeout(id); }, [searchInput]); // eslint-disable-line

  const [editing, setEditing] = React.useState(null);   // user | "new" | null
  const [form, setForm] = React.useState(blankUser);
  const [errors, setErrors] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [info, setInfo] = React.useState(null);
  const [permissionsFor, setPermissionsFor] = React.useState(null); // permissions slide-in
  const [toDelete, setToDelete] = React.useState(null);
  const [toArchive, setToArchive] = React.useState(null);
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [bulkOpen, setBulkOpen] = React.useState(false);
  // Invitation link generated after an "invite by link" add.
  const [invitationLink, setInvitationLink] = React.useState("");
  // Stealth confirmation modal (single + bulk), matching legacy guarded toggles.
  const [stealthConfirm, setStealthConfirm] = React.useState(null); // { user, next } | { bulk:true, next }
  const [stealthBusy, setStealthBusy] = React.useState(false);
  const [exportOpen, setExportOpen] = React.useState(false);
  const [exportBusy, setExportBusy] = React.useState(false);
  // Cards vs table. Remembered, because which one someone wants is a working
  // style, not a per-visit decision.
  const [view, setView] = React.useState(() => {
    try { return localStorage.getItem(VIEW_KEY) === "table" ? "table" : "cards"; } catch (_) { return "cards"; }
  });
  const chooseView = React.useCallback((next) => {
    setView(next);
    try { localStorage.setItem(VIEW_KEY, next); } catch (_) { /* private mode — the choice just won't stick */ }
  }, []);
  const exportRef = React.useRef(null);
  const exportMenuRef = React.useRef(null);

  React.useEffect(() => {
    if (!exportOpen) return undefined;
    const onDoc = (e) => {
      if (exportRef.current?.contains(e.target) || exportMenuRef.current?.contains(e.target)) return;
      setExportOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [exportOpen]);

  const roleOptions = [{ value: "", label: "All roles" }, ...d.roles];
  const canAdd = d.canAddTeamMembers();
  const isAdmin = d.user?.role === "org_admin" || d.user?.role === "main_admin";
  const currentUserId = d.user?.id || d.user?._id;
  const showCrm = isCrmEnabledForOrg(window.__orgProfile) && d.user?.role !== "org_user";

  // "Active" is the default, so it alone doesn't count as a filter being on.
  const filtersActive = !!(d.search || d.role || d.desktopAgent || d.attention) || d.status !== "active";
  const resetFilters = () => {
    setSearchInput(""); d.setSearch(""); d.setRole(""); d.setDesktopAgent(""); d.setStatus("active");
    d.setAttention("");
  };

  // Panel figures come from `scoped` — the whole org under the current filters —
  // never from the current page, so they don't change as you page through.
  const composition = React.useMemo(
    () => compositionBreakdown(d.scoped, { roles: d.roles, structure: d.structure }),
    [d.scoped, d.roles, d.structure],
  );
  const coverage = React.useMemo(() => coverageMetrics(d.scoped, d.insightContext), [d.scoped, d.insightContext]);
  const attention = React.useMemo(
    () => attentionCounts(d.scoped, d.insightContext, { statusAgnostic: d.scopedAnyStatus }),
    [d.scoped, d.scopedAnyStatus, d.insightContext],
  );

  // A bucket backed by a real server filter drives that control instead of
  // layering a second filter on top of it, so the two can't disagree.
  const bucketActive = React.useCallback(
    (b) => (b.statusFilter ? d.status === b.statusFilter : d.attention === b.key),
    [d.status, d.attention],
  );
  const toggleBucket = React.useCallback((b) => {
    if (b.statusFilter) {
      d.setAttention("");
      d.setStatus(d.status === b.statusFilter ? "active" : b.statusFilter);
      return;
    }
    d.setAttention(d.attention === b.key ? "" : b.key);
  }, [d]);
  const levels = React.useMemo(() => orgLevels(d.structure, d.scoped), [d.structure, d.scoped]);
  const spans = React.useMemo(() => managerSpans(d.scoped, d.allUsers), [d.scoped, d.allUsers]);
  const orphanCount = React.useMemo(
    () => d.scoped.filter((u) => isOrphan(u, d.insightContext)).length,
    [d.scoped, d.insightContext],
  );
  const directReports = React.useMemo(
    () => getMembersReportingTo(currentUserId, d.allUsers),
    [currentUserId, d.allUsers],
  );
  const canExport = isAdmin || directReports.length > 0;

  const exportMembers = async (format) => {
    setExportOpen(false);
    setExportBusy(true);
    const toastId = toast.loading("Preparing export…");
    try {
      const allRes = await d.api.get("/api/v1/users/all?includeAppVersion=1");
      const all = allRes.data?.data?.users || [];
      let users;
      if (d.desktopAgent === "not_active") {
        // Server-side: 0 tracked hours today
        const res = await d.api.get("/api/v1/users?desktopAgent=not_active&limit=5000&page=1");
        const list = res.data?.data?.users || [];
        users = isAdmin ? list : getMembersReportingTo(currentUserId, list);
      } else {
        users = isAdmin ? all : getMembersReportingTo(currentUserId, all);
        if (d.desktopAgent === "yes") users = users.filter((u) => u.hasDesktopAgent);
        else if (d.desktopAgent === "no") users = users.filter((u) => !u.hasDesktopAgent);
      }
      if (!users.length) {
        toast.error("No members to export", { id: toastId });
        return;
      }
      const rows = buildMemberExportRows(users, d.roles, all);
      if (format === "csv") exportMembersCsv(rows);
      else exportMembersXlsx(rows);
      toast.success(`Exported ${users.length} member${users.length === 1 ? "" : "s"}`, { id: toastId });
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to export members", { id: toastId });
    } finally {
      setExportBusy(false);
    }
  };

  // Slot-limit gate (V1 parity): if slots are full, ensureSlot() opens the
  // upgrade (free) or slot-purchase (paid) modal and returns false.
  const openNew = async () => {
    const ok = await slotGate.ensureSlot();
    if (!ok) return;
    setForm(blankUser); setErrors({}); setEditing("new");
  };
  const openEdit = (u) => {
    setErrors({});
    setForm({
      firstName: u.firstName || "", lastName: u.lastName || "", email: u.email || "",
      stubEmail: u.stubEmail || "", role: u.role || "org_user", phone: u.phone || "", department: u.department || "",
      position: u.position || "", reportingManagers: normalizeReportingManagerIds(u),
      billingEnabled: u.billingEnabled !== false, password: "", stealth_mode: !!u.stealth_mode,
      invitationMethod: "manual",
      permissions: u.permissions || blankUser.permissions,
    });
    setEditing(u);
  };

  const save = async () => {
    // Inline validation — sets per-field errors so the user sees exactly what's wrong
    // (a top-right toast alone is easy to miss).
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = "First name is required";
    if (!form.email.trim()) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = "Enter a valid email";
    const stubTrim = form.stubEmail?.trim();
    if (stubTrim && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(stubTrim)) errs.stubEmail = "Enter a valid stub email";
    else if (stubTrim && stubTrim.toLowerCase() === form.email.trim().toLowerCase()) errs.stubEmail = "Stub email must differ from login email";
    if (form.role !== "org_admin" && !String(form.position || "").trim()) errs.position = "Position is required for team members";
    const managerIds = normalizeReportingManagerIds(form.reportingManagers);
    if (form.role !== "org_admin" && String(form.position || "").trim() && managerIds.length === 0) {
      errs.reportingManagers = "Reporting manager is required when a position is assigned";
    }
    // Password mandatory ONLY for the manual single-add method (min 8) — email /
    // invitation-link methods let the member set their own password later.
    if (editing === "new" && form.invitationMethod === "manual") {
      if (!form.password || !form.password.trim()) errs.password = "Password is required";
      else if (form.password.length < 8) errs.password = "Password must be at least 8 characters";
    } else if (editing && editing !== "new" && form.password?.trim()) {
      if (form.password.length < 8) errs.password = "Password must be at least 8 characters";
    }
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error(Object.values(errs)[0]); return; }
    setSaving(true);
    try {
      const payload = {
        firstName: form.firstName.trim(), lastName: form.lastName.trim(), email: form.email.trim(),
        stubEmail: form.stubEmail?.trim() || null,
        role: form.role, phone: form.phone, department: form.department, position: form.position,
        reportingManagers: managerIds,
        reportingManager: managerIds[0] || null,
        billingEnabled: form.billingEnabled, stealth_mode: form.stealth_mode,
        permissions: form.permissions,
      };
      if (editing === "new") {
        // Backend requires a valid organizationId on create — derive it from the
        // logged-in user (can be an object or a string), exactly like the legacy page.
        const orgId = typeof d.user?.organizationId === "object"
          ? (d.user.organizationId?._id || d.user.organizationId?.id)
          : d.user?.organizationId;
        if (!orgId) { toast.error("Could not determine your organization"); setSaving(false); return; }
        payload.organizationId = orgId;
        if (form.password) payload.password = form.password;
        const createRes = await api().post("/api/v1/users", payload);
        toast.success("Member added");

        // Follow-up for email / invitation-link methods (legacy parity).
        const newUserId = createRes.data?.data?.user?._id || createRes.data?.data?.user?.id;
        if (form.invitationMethod === "email") {
          try {
            await api().post("/api/v1/users/send-invitation-email", {
              userId: newUserId, email: form.email.trim(), organizationId: orgId,
            });
            toast.success("Invitation email sent!");
          } catch (_) { toast.error("Member added, but failed to send invitation email"); }
        } else if (form.invitationMethod === "link") {
          try {
            const inviteRes = await api().post("/api/v1/organizations/invitation-link", {
              organizationId: orgId, email: form.email.trim(),
            });
            const link = inviteRes.data?.data?.invitationLink || inviteRes.data?.data?.link || "";
            setInvitationLink(link);
          } catch (_) { toast.error("Member added, but failed to generate invitation link"); }
        }
      } else {
        const trimmedPassword = form.password?.trim();
        if (trimmedPassword) payload.password = trimmedPassword;
        await api().put(`/api/v1/users/${editing.id || editing._id}`, payload);
        toast.success("Member updated");
      }
      setEditing(null); d.refresh();
    } catch (err) {
      const fieldErrs = mapBackendFieldErrors(err);
      if (Object.keys(fieldErrs).length) {
        setErrors((prev) => ({ ...prev, ...fieldErrs }));
        toast.error(Object.values(fieldErrs)[0]);
      } else {
        toast.error(err.response?.data?.error?.message || err.response?.data?.message || "Failed to save member");
      }
    } finally { setSaving(false); }
    function api() { return d.api; }
  };

  // Stealth toggle now goes through a confirmation modal (legacy parity) instead
  // of firing immediately.
  const requestStealth = (u) => setStealthConfirm({ user: u, next: !u.stealth_mode });
  const requestBulkStealth = (next) => setStealthConfirm({ bulk: true, next });

  const confirmStealth = async () => {
    if (!stealthConfirm) return;
    setStealthBusy(true);
    try {
      if (stealthConfirm.bulk) {
        await d.api.put("/api/v1/users/bulk-stealth-mode", { stealth_mode: stealthConfirm.next });
        toast.success(`Background mode ${stealthConfirm.next ? "enabled" : "disabled"} for all members`);
      } else {
        const u = stealthConfirm.user;
        await d.api.put(`/api/v1/users/${u.id || u._id}`, { stealth_mode: stealthConfirm.next });
        toast.success(`Background mode ${stealthConfirm.next ? "enabled" : "disabled"}`);
      }
      setStealthConfirm(null); d.refresh();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.response?.data?.message || "Failed to update background mode");
    } finally { setStealthBusy(false); }
  };

  // Archive a member (soft remove) — legacy parity.
  const doArchive = async () => {
    if (!toArchive) return;
    try {
      await d.api.put(`/api/v1/users/${toArchive.id || toArchive._id}`, { status: "archived" });
      toast.success("Member archived");
      setToArchive(null); d.refresh();
    } catch (err) { toast.error(err.response?.data?.message || "Failed to archive"); }
  };

  const columns = React.useMemo(
    () => teamColumns({
      t, roles: d.roles, allUsers: d.allUsers, isAdmin, currentUserId, showCrm,
      onInfo: setInfo,
      onEdit: openEdit,
      onDelete: setToDelete,
      onArchive: setToArchive,
      onStealth: requestStealth,
      onPermissions: setPermissionsFor,
    }),
    // openEdit / requestStealth are stable enough for the row cluster; the data
    // they read comes through the arguments above.
    [t, d.roles, d.allUsers, isAdmin, currentUserId, showCrm], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Shared by both views so an empty list offers the same way out either way.
  const emptyState = (
    <EmptyState
      icon={<Users size={22} />}
      title="No members found"
      description={filtersActive ? "No members match these filters." : "Add your first team member."}
      action={filtersActive
        ? <Button variant="secondary" onClick={resetFilters}>Clear filters</Button>
        : (canAdd ? <Button variant="primary" leftIcon={<UserPlus size={16} />} onClick={openNew}>Add member</Button> : null)}
    />
  );

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await d.api.delete(`/api/v1/users/${toDelete.id || toDelete._id}`);
      toast.success("Member removed");
      setToDelete(null); d.refresh();
    } catch (err) { toast.error(err.response?.data?.message || "Failed to remove"); }
  };

  return (
    <PageShell
      eyebrow="Workspace · People"
      title={structureView ? "Org structure (old)" : "Team"}
      subtitle={structureView
        ? "Members grouped by level — the interactive chart lives under Org structure"
        : `${d.stats.total} members · ${d.stats.admins} admins`}
      icon={structureView ? <Network size={22} /> : <Users size={22} />}
      topRight={!structureView ? (
        <div className="tg-filterbar">
          <label className="tg-tool tg-tool-search">
            <Search size={14} />
            <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search members" aria-label="Search members" />
          </label>
          <span className="tg-tool-sep" />
          <ToolSelect
            icon={<ListFilter size={14} />}
            value={d.status}
            options={MEMBER_STATUSES}
            onChange={d.setStatus}
            placement="bottomRight"
            panelWidth={190}
          />
          <ToolSelect
            icon={<UserCog size={14} />}
            value={d.role}
            options={roleOptions}
            onChange={d.setRole}
            placement="bottomRight"
            panelWidth={190}
          />
          <ToolSelect
            icon={<Monitor size={14} />}
            value={d.desktopAgent}
            options={AGENT_FILTERS}
            onChange={d.setDesktopAgent}
            placement="bottomRight"
            panelWidth={200}
          />
        </div>
      ) : null}
      actions={
        <>
          <Button
            variant="secondary"
            leftIcon={<Network size={15} />}
            onClick={() => {
              // Open the Team Structure tab in Settings; respect the v1 shell prefix.
              const base = DASHBOARD_BASE;
              navigate(`${base}/settings?section=team`);
            }}
          >
            Team Structure
          </Button>
          {/* Invite-link drawer (legacy invitation-link mode) */}
          {/* {canAdd && <Button variant="secondary" leftIcon={<Link2 size={15} />} onClick={() => setInviteOpen(true)}>Invite link</Button>} */}
          {/* Bulk background mode — an action on all members, not a filter, so it
              lives with the other actions instead of in the filter bar. */}
          {!structureView && isAdmin && (
            <ToolSelect
              icon={<EyeOff size={14} />}
              value={null}
              placeholder="Background mode"
              options={[
                { value: "on", label: "Enable for all members" },
                { value: "off", label: "Disable for all members" },
              ]}
              onChange={(v) => requestBulkStealth(v === "on")}
              placement="bottomRight"
              panelWidth={220}
            />
          )}
          {/* Add buttons gated on permission (admins, or org_user w/ addTeamMember). */}
          {!structureView && canAdd && <Button variant="secondary" leftIcon={<Upload size={15} />} onClick={() => setBulkOpen(true)}>Import</Button>}
          {!structureView && canExport && (
            <div ref={exportRef}>
              <Button variant="secondary" leftIcon={<Download size={15} />} rightIcon={<ChevronDown size={14} />} onClick={() => setExportOpen((v) => !v)} disabled={exportBusy}>
                {exportBusy ? "Exporting…" : "Export"}
              </Button>
              <ExportDropdown
                t={t}
                open={exportOpen}
                anchorRef={exportRef}
                menuRef={exportMenuRef}
                onCsv={() => exportMembers("csv")}
                onXlsx={() => exportMembers("xlsx")}
              />
            </div>
          )}
          {!structureView && canAdd && <Button variant="primary" leftIcon={<UserPlus size={16} />} onClick={openNew}>Add member</Button>}
        </>
      }
    >
      {structureView && (
        <OrgStructure t={t} structure={d.structure} users={d.allUsers} roles={d.roles} onMember={(u) => setInfo(u)} />
      )}

      {!structureView && (
      <>
      <TeamPulse
        composition={composition}
        coverage={coverage}
        attention={attention}
        isActive={bucketActive}
        onToggle={toggleBucket}
      />

      <div className="tg-team-structure" style={{ marginBottom: t.s[4] }}>
        <OrgLevelsPanel
          levels={levels}
          hasStructure={(d.structure || []).length > 0}
          onMember={setInfo}
          onConfigure={isAdmin ? () => navigate(`${DASHBOARD_BASE}/settings?section=team`) : null}
        />
        <ReportingLinesPanel
          spans={spans}
          orphanCount={orphanCount}
          onMember={setInfo}
          onShowOrphans={() => d.setAttention("nomanager")}
        />
      </div>

      {/* Active quick-filter + view switch, directly above the list they act on. */}
      <div style={{ display: "flex", alignItems: "center", gap: t.s[2], marginBottom: t.s[3], flexWrap: "wrap" }}>
        {d.attention && (
          <span style={{
            height: 28, display: "inline-flex", alignItems: "center", gap: 6, padding: "0 6px 0 10px",
            borderRadius: t.r.sm, background: t.c.primarySoft, color: t.c.primary,
            fontSize: t.font.size.sm, fontWeight: t.font.weight.medium,
          }}>
            {attentionLabel(d.attention)}
            <button
              type="button"
              onClick={() => d.setAttention("")}
              aria-label="Clear quick filter"
              style={{ display: "inline-flex", background: "none", border: "none", padding: 2, cursor: "pointer", color: "inherit" }}
            ><X size={12} /></button>
          </span>
        )}
        <div style={{ marginLeft: "auto", display: "inline-flex", gap: 2, padding: 2, borderRadius: t.r.sm, background: t.c.surfaceMuted, border: `1px solid ${t.c.border}` }}>
          {[
            { key: "cards", label: "Cards", icon: <LayoutGrid size={13} /> },
            { key: "table", label: "Table", icon: <LayoutList size={13} /> },
          ].map((v) => (
            <button
              key={v.key}
              type="button"
              onClick={() => chooseView(v.key)}
              aria-pressed={view === v.key}
              style={{
                height: 26, padding: "0 11px", border: "none", borderRadius: t.r.xs, cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 6,
                fontSize: t.font.size.sm, fontWeight: t.font.weight.medium, fontFamily: t.font.family,
                background: view === v.key ? t.c.surface : "transparent",
                color: view === v.key ? t.c.text : t.c.textSecondary,
                boxShadow: view === v.key ? t.shadow.sm : "none",
              }}
            >{v.icon}{v.label}</button>
          ))}
        </div>
      </div>

      {view === "cards" ? (
        <>
          <MemberCardGrid
            members={d.users}
            loading={d.loading}
            skeletonCount={Math.min(d.pageSize, 8)}
            t={t}
            roles={d.roles}
            allUsers={d.allUsers}
            showCrm={showCrm}
            isAdmin={isAdmin}
            currentUserId={currentUserId}
            insightContext={d.insightContext}
            emptyState={<Card padding={0}>{emptyState}</Card>}
            onInfo={setInfo}
            onEdit={openEdit}
            onDelete={setToDelete}
            onArchive={setToArchive}
            onStealth={requestStealth}
            onPermissions={setPermissionsFor}
          />
          {d.users.length > 0 && (
            <div style={{ marginTop: t.s[3] }}>
              <Card padding={0}>
                <Pagination
                  page={d.page} pages={d.pages} total={d.total} pageLabel="members"
                  onChange={d.setPage}
                  pageSize={d.pageSize} onPageSizeChange={d.setPageSize}
                  shown={d.users.length}
                />
              </Card>
            </div>
          )}
        </>
      ) : (
        <Card padding={0}>
          <DataTable
            columns={columns}
            rows={d.users}
            rowKey={(u) => u.id || u._id}
            loading={d.loading}
            skeletonRows={8}
            onRowClick={(u) => setInfo(u)}
            emptyState={emptyState}
          />
          <Pagination
            page={d.page} pages={d.pages} total={d.total} pageLabel="members"
            onChange={d.setPage}
            pageSize={d.pageSize} onPageSizeChange={d.setPageSize}
            shown={d.users.length}
          />
        </Card>
      )}

      {/* Say it out loud rather than leaving a page that silently comes up short. */}
      {d.scopedOut > 0 && (
        <div style={{ marginTop: t.s[3], fontSize: t.font.size.sm, color: t.c.textTertiary }}>
          {d.scopedOut} member{d.scopedOut === 1 ? "" : "s"} on this page {d.scopedOut === 1 ? "is" : "are"} outside your part of the org structure and hidden.
        </div>
      )}
      </>
      )}

      {/* Add / Edit drawer */}
      <MemberFormDrawer
        open={!!editing} onClose={() => setEditing(null)} onSave={save} saving={saving}
        editing={editing} form={form} setForm={setForm} errors={errors} setErrors={setErrors}
        roles={d.roles} allUsers={d.allUsers} structure={d.structure} currentUser={d.user}
        getAvailableReportingManagers={d.getAvailableReportingManagers}
        onAddPosition={() => navigate(`${DASHBOARD_BASE}/settings?section=team`)}
      />

      {/* Permissions + reporting slide-in, opened from the Access count in a row */}
      <MemberPermissionsPanel
        member={permissionsFor}
        roles={d.roles}
        allUsers={d.allUsers}
        structure={d.structure}
        showCrm={showCrm}
        onClose={() => setPermissionsFor(null)}
        onEdit={() => { const u = permissionsFor; setPermissionsFor(null); openEdit(u); }}
      />

      {/* Info drawer */}
      <Drawer open={!!info} onClose={() => setInfo(null)} title={info ? memberName(info) : ""} subtitle="Member profile" icon={<Users size={18} />} width={460}
        footer={info ? <>
          <Button variant="secondary" leftIcon={<ShieldCheck size={14} />} onClick={() => { const u = info; setInfo(null); setPermissionsFor(u); }}>Access</Button>
          <Button variant="secondary" leftIcon={<Pencil size={14} />} onClick={() => { const u = info; setInfo(null); openEdit(u); }}>Edit</Button>
          <Button variant="primary" onClick={() => setInfo(null)}>Close</Button>
        </> : null}>
        {info && <MemberInfo t={t} u={info} roles={d.roles} allUsers={d.allUsers} />}
      </Drawer>

      {/* Delete confirm */}
      <Modal open={!!toDelete} onClose={() => setToDelete(null)} title="Remove member" icon={<AlertTriangle size={18} />} width={440}
        footer={<><Button variant="secondary" onClick={() => setToDelete(null)}>Cancel</Button><Button variant="danger" onClick={doDelete}>Remove</Button></>}>
        <p style={{ fontSize: t.font.size.md, color: t.c.textSecondary, lineHeight: 1.6, margin: 0 }}>
          Remove <strong style={{ color: t.c.text }}>{toDelete?.firstName} {toDelete?.lastName}</strong> from the team? This cannot be undone.
        </p>
      </Modal>

      {/* Archive confirm */}
      <Modal open={!!toArchive} onClose={() => setToArchive(null)} title="Archive member" icon={<Archive size={18} />} width={440}
        footer={<><Button variant="secondary" onClick={() => setToArchive(null)}>Cancel</Button><Button variant="primary" onClick={doArchive}>Archive</Button></>}>
        <p style={{ fontSize: t.font.size.md, color: t.c.textSecondary, lineHeight: 1.6, margin: 0 }}>
          Archive <strong style={{ color: t.c.text }}>{toArchive?.firstName} {toArchive?.lastName}</strong>? They’ll be hidden from the team but their data is retained.
        </p>
      </Modal>

      {/* Stealth / Background-mode confirm (single + bulk) */}
      <Modal open={!!stealthConfirm} onClose={() => !stealthBusy && setStealthConfirm(null)} title="Background mode" icon={<EyeOff size={18} />} width={440}
        footer={<>
          <Button variant="secondary" onClick={() => setStealthConfirm(null)} disabled={stealthBusy}>Cancel</Button>
          <Button variant="primary" onClick={confirmStealth} disabled={stealthBusy}>{stealthBusy ? "Applying…" : "Confirm"}</Button>
        </>}>
        <p style={{ fontSize: t.font.size.md, color: t.c.textSecondary, lineHeight: 1.6, margin: 0 }}>
          {stealthConfirm?.bulk
            ? <>{stealthConfirm?.next ? "Enable" : "Disable"} background (stealth) mode for <strong style={{ color: t.c.text }}>all members</strong>?</>
            : <>{stealthConfirm?.next ? "Enable" : "Disable"} background (stealth) mode for <strong style={{ color: t.c.text }}>{stealthConfirm?.user?.firstName} {stealthConfirm?.user?.lastName}</strong>?</>}
        </p>
      </Modal>

      {/* Invitation link generated after an "invite by link" add */}
      <Modal open={!!invitationLink} onClose={() => setInvitationLink("")} title="Invitation link" icon={<Link2 size={18} />} width={520}
        footer={<Button variant="primary" onClick={() => setInvitationLink("")}>Done</Button>}>
        <p style={{ fontSize: t.font.size.md, color: t.c.textSecondary, lineHeight: 1.6, marginTop: 0 }}>
          Member created. Share this link so they can set their password and join:
        </p>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1, display: "flex", alignItems: "center", padding: "0 12px", height: 40, borderRadius: t.r.sm, border: `1px solid ${t.c.border}`, background: t.c.surfaceMuted, fontSize: t.font.size.sm, color: t.c.textSecondary, overflow: "hidden" }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{invitationLink}</span>
          </div>
          <Button variant="secondary" leftIcon={<Copy size={15} />} onClick={() => { navigator.clipboard.writeText(invitationLink); toast.success("Copied"); }}>Copy</Button>
        </div>
      </Modal>

      {/* Invite link drawer */}
      <InviteDrawer t={t} api={d.api} open={inviteOpen} onClose={() => setInviteOpen(false)} />

      <BulkImportDrawer
        t={t}
        api={d.api}
        user={d.user}
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onDone={() => { setBulkOpen(false); d.refresh(); }}
        structure={d.structure}
        allUsers={d.allUsers}
        roles={d.roles}
        onAddPosition={() => navigate(`${DASHBOARD_BASE}/settings?section=team`)}
      />

      {/* Slot-limit upgrade / slot-purchase modals (V1 parity, payment logic untouched) */}
      {slotGate.modals}
    </PageShell>
  );
}

// Org structure — members grouped by reporting level (from team-structure),
// falling back to grouping by position when no structure is defined.
function OrgStructure({ t, structure, users, roles, onMember }) {
  const byPosition = React.useMemo(() => {
    const map = {};
    (users || []).forEach((u) => {
      const key = u.position || "Unassigned";
      (map[key] = map[key] || []).push(u);
    });
    return map;
  }, [users]);

  // Ordered level list from structure; else positions present in data.
  const levels = React.useMemo(() => {
    if (structure && structure.length) {
      return structure.map((lvl, i) => ({
        name: lvl.levelName || `Level ${i + 1}`,
        positions: (lvl.positions || []).map((p) => p.roleName),
      }));
    }
    return [{ name: "Team", positions: Object.keys(byPosition) }];
  }, [structure, byPosition]);

  const isEmpty = !users || users.length === 0;
  if (isEmpty) {
    return <Card padding={0}><EmptyState icon={<Network size={22} />} title="No structure yet" description="Add members and assign positions to see the org structure." /></Card>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: t.s[5] }}>
      {levels.map((lvl, li) => {
        const members = lvl.positions.flatMap((pos) => byPosition[pos] || []);
        if (members.length === 0) return null;
        return (
          <motion.div key={li} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: li * 0.06, ease: t.motion.ease.out }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: t.s[3] }}>
              <span style={{ fontSize: t.font.size.sm, fontWeight: 700, color: t.c.textSecondary, textTransform: "uppercase", letterSpacing: "0.05em" }}>{lvl.name}</span>
              <span style={{ flex: 1, height: 1, background: t.c.border }} />
              <Badge tone="neutral">{members.length}</Badge>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: t.s[3] }}>
              {members.map((u) => {
                const name = `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email;
                return (
                  <Card key={u.id || u._id} interactive padding={14} onClick={() => onMember(u)}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <Avatar name={name} src={u.avatar} size={38} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: t.font.size.md, fontWeight: 600, color: t.c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
                        <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.position || roleLabel(roles, u.role)}</div>
                      </div>
                      {u.role === "org_admin" && <Crown size={14} style={{ color: t.accents[3], flexShrink: 0 }} />}
                    </div>
                  </Card>
                );
              })}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

function MemberInfo({ t, u, roles, allUsers = [] }) {
  const reportingManagers = resolveReportingManagers(u, allUsers);
  const Row = ({ icon, label, children }) => (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, paddingBottom: t.s[3], marginBottom: t.s[3], borderBottom: `1px solid ${t.c.border}` }}>
      <span style={{ color: t.c.textTertiary, marginTop: 1 }}>{icon}</span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: t.font.size.md, color: t.c.text, marginTop: 2 }}>{children}</div>
      </div>
    </div>
  );
  const name = `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email;
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: t.s[5] }}>
        <Avatar name={name} src={u.avatar} size={56} />
        <div>
          <div style={{ fontSize: t.font.size.xl, fontWeight: 700, color: t.c.text }}>{name}</div>
          <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
            <Badge tone={u.role === "org_admin" ? "warning" : "brand"}>{roleLabel(roles, u.role)}</Badge>
            {u.stealth_mode && <Badge tone="info"><EyeOff size={11} /> Stealth</Badge>}
            {u.hasDesktopAgent && <Badge tone="neutral">Desktop Agent</Badge>}
            {!u.hasDesktopAgent && u.latestAppVersion && <Badge tone="neutral">v{u.latestAppVersion}</Badge>}
          </div>
        </div>
      </div>
      <Row icon={<Mail size={15} />} label={u.stubEmail ? "Stub email" : "Email"}>{displayMemberEmail(u)}</Row>
      {u.stubEmail && u.email && (
        <Row icon={<Mail size={15} />} label="Login email">{u.email}</Row>
      )}
      <Row icon={<Briefcase size={15} />} label="Position">{u.position || "—"}</Row>
      <Row icon={<UserCog size={15} />} label="Reporting managers">
        {reportingManagers.length > 0 ? reportingManagers.map(formatManagerName).join(", ") : "—"}
      </Row>
      <Row icon={<Building2 size={15} />} label="Department">{u.department || "—"}</Row>
      <Row icon={<Phone size={15} />} label="Phone">{u.phone || "—"}</Row>
      <Row icon={<Shield size={15} />} label="Role">{roleLabel(roles, u.role)}</Row>
      <Row icon={<Monitor size={15} />} label={u.hasDesktopAgent ? "Desktop Agent" : "App Version"}>
        {u.hasDesktopAgent ? "Yes" : (u.latestAppVersion ? `v${u.latestAppVersion}` : "—")}
      </Row>
    </div>
  );
}

function InviteDrawer({ t, api, open, onClose }) {
  const [link, setLink] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const generate = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.post("/api/v1/organizations/invitation-link", {});
      setLink(res.data?.data?.invitationLink || res.data?.data?.link || res.data?.invitationLink || "");
    } catch (err) { toast.error(err.response?.data?.message || "Failed to generate link"); }
    finally { setLoading(false); }
  }, [api]);

  React.useEffect(() => { if (open && !link) generate(); }, [open, link, generate]);

  const copy = () => { if (!link) return; navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1600); };

  return (
    <Drawer open={open} onClose={onClose} title="Invite link" subtitle="Share to let people join your team" icon={<Link2 size={18} />} width={440}>
      <p style={{ fontSize: t.font.size.md, color: t.c.textSecondary, lineHeight: 1.6, marginTop: 0 }}>
        Anyone with this link can request to join your organization. You can regenerate it any time.
      </p>
      <div style={{ display: "flex", gap: 8, marginTop: t.s[4] }}>
        <div style={{ flex: 1, display: "flex", alignItems: "center", padding: "0 12px", height: 40, borderRadius: t.r.sm, border: `1px solid ${t.c.border}`, background: t.c.surfaceMuted, fontSize: t.font.size.sm, color: t.c.textSecondary, overflow: "hidden" }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{loading ? "Generating…" : link || "—"}</span>
        </div>
        <Button variant="primary" leftIcon={copied ? <Check size={15} /> : <Copy size={15} />} onClick={copy} disabled={!link}>{copied ? "Copied" : "Copy"}</Button>
      </div>
      <div style={{ marginTop: t.s[4] }}>
        <Button variant="secondary" onClick={generate} disabled={loading}>Regenerate link</Button>
      </div>
    </Drawer>
  );
}
